import { useAuth } from "@home-health-notify/auth";
import { ChangePasswordForm } from "@home-health-notify/auth/web";
import { useTranslation } from "hhn-commons";
import { useSnackbar } from "notistack";
import React from "react";
import { useNavigate } from "react-router";

import CommonDialog from "../../common/CommonDialog/CommonDialog";
import { ROUTES } from "../../routes";

interface IChangePasswordModalProps {
  closeModal: () => void;
}

/**
 * Thin host wrapper around the package's <ChangePasswordForm />. The form
 * owns the inputs, validation, criteria checklist, submission, and error
 * mapping (FE-owned strings via the CognitoChangePasswordError.kind
 * discriminator). The host owns:
 *   - the dialog shell (MUI CommonDialog)
 *   - the success snackbar
 *   - the post-success force-logout (security: user must re-auth with the
 *     new password; also invalidates any other tab's session under the
 *     shared cookie domain via the cross-tab visibilitychange listener
 *     in AuthProvider)
 */
export const ChangePasswordModal = ({
  closeModal,
}: IChangePasswordModalProps) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  // Federated: the new password must be re-entered on the next sign-in,
  // which only happens once Cognito's SSO cookie is gone.
  const { signOutFederated: logoutUser } = useAuth();
  const navigate = useNavigate();
  const tNs = (key: string) => t(key, { ns: "changePassword" });

  return (
    <CommonDialog
      isDialogOpen={true}
      onDialogClose={closeModal}
      title={"Change Password"}
      titleAlignment="center"
      maxWidth="xs"
    >
      <ChangePasswordForm
        onSuccess={() => {
          enqueueSnackbar(tNs("changePasswordSuccess"), {
            autoHideDuration: 3000,
          });
          closeModal();
          // Delay so the user sees the snackbar before getting bounced
          // to /login. logoutUser + navigate mirror the existing
          // sign-out flow in Organizations.tsx.
          setTimeout(() => {
            logoutUser().finally(() => {
              navigate(ROUTES.LOGIN, { replace: true });
            });
          }, 1500);
        }}
        onCancel={closeModal}
      />
    </CommonDialog>
  );
};
